import type { AudioId } from '../../shared/types/ids';

/**
 * How a spoken line finds its recording (Slice A″, PS5-6).
 *
 * **Why the text is the key.** Every line in this product reaches narration as a string and
 * nothing else: `NarrationService.speak(text, options)` is the seam, and the mission runtime that
 * calls it is frozen. There is no line ID travelling alongside the words and there is no place to
 * put one without changing a frozen surface. So the key is computed *from* the words.
 *
 * **What that buys.** A hand-written line ID lets the text and the recording drift apart silently:
 * somebody rewords a question, nobody re-bakes, and a five-year-old is asked one thing on screen
 * and another out loud. A content-derived key cannot do that — edited words are a different key,
 * the manifest has no entry for it, and the line falls back to the browser voice for that line
 * only. Staleness becomes a fallback rather than a lie.
 *
 * **What it costs.** The ID is not human-readable. The file *behind* it is (the bake tool names
 * files with a readable slug and records the text in the generated manifest beside every entry),
 * so a person can still find the recording of a line; the ID is machinery.
 *
 * Nothing here reaches a network, a hash library, or the platform's storage: it is arithmetic over
 * the characters, so the bake tool and the browser compute the same key from the same sentence.
 */

/**
 * The line, as a child hears it.
 *
 * Authored content is prettier-wrapped, so the same sentence can arrive with a newline and two
 * spaces inside it. Whitespace is not a sound; everything else is (case and punctuation change the
 * delivery, so they stay).
 */
export function narrationLineKey(text: string): string {
  return text.replace(/\s+/gu, ' ').trim();
}

const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK_64 = 0xffffffffffffffffn;

/** FNV-1a over the UTF-16 code units, 64-bit, lower-case hex. */
function fnv1a64Hex(value: string): string {
  let hash = FNV_OFFSET_BASIS;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= BigInt(value.charCodeAt(index));
    hash = (hash * FNV_PRIME) & MASK_64;
  }
  return hash.toString(16).padStart(16, '0');
}

/**
 * The manifest ID of the recording for `text`, or null when there is no line to say.
 *
 * `narration.[16 hex].v1` satisfies the shared D-014 asset-ID shape, so a baked line is an
 * ordinary theme asset with ordinary validation — which is the whole point of PS5-7's "baked files
 * are ordinary theme assets". The version token is part of the ID convention rather than a
 * re-recording counter: a *changed line* is a changed key, so `v1` is where every line stays.
 */
export function bakedNarrationAudioId(text: string): AudioId | null {
  const key = narrationLineKey(text);
  if (key.length === 0) return null;
  return `narration.${fnv1a64Hex(key)}.v1`;
}
